// src/lib/routing/detailRouteResolution.ts
//
// Record-detail deep-link resolution invariants (projects / deals).
// ==================================================================
// A record-detail route (`#/projects/record/:id`, `#/deals/record/:id`) seeds
// its detail-open flag TRUE from the URL hash and then fetches the record on
// mount / hashchange. The detail view renders a loading spinner while
// `detailOpen && !detailData`. Two defects made that spinner hang FOREVER:
//
//  1. THE HANG: the fetch's `.catch(() => {})` silently swallowed a 404/error,
//     so `detailData` stayed null while `detailOpen` stayed true — the spinner
//     never cleared. The catch MUST instead transition OUT of the loading state
//     (close the detail + bounce the hash to the list route).
//
//  2. STALE TUT- FIXTURE: `TUT-`-prefixed ids are client-only tutorial fixtures
//     served ONLY by the WalkthroughPlayer fetch-shim while a walkthrough runs.
//     A hard-refresh / abnormal teardown can leave a `#/projects/record/TUT-…`
//     hash alive after the shim is gone; on a shim-less boot the real API 404s
//     (by design — the server has no TUT branch). Fetching it is pointless and
//     was the concrete trigger of the infinite spinner.
//
// These pure helpers are the single tested source of both invariants; the App
// shell wires them into the mount effect AND the runtime hashchange handler.

import { EPHEMERAL_ID_PREFIX } from '../../views/knowledge-base/tutorialFixtures'

/**
 * True when a record-detail fetch MUST be skipped: the id is an ephemeral
 * tutorial fixture (`TUT-…`) AND no walkthrough is currently active. On a
 * shim-less boot such an id only 404s, so we skip the fetch and bounce to the
 * list route instead of opening a detail view that can never load.
 */
export function shouldSkipStaleTutorialFetch(
  rawId: string | null | undefined,
  walkthroughActive: boolean,
): boolean {
  if (!rawId) return false
  return rawId.startsWith(EPHEMERAL_ID_PREFIX) && !walkthroughActive
}

export type DetailFetchOutcome =
  /** id is a stale TUT- fixture on a shim-less boot — never fetch it. */
  | { kind: 'skip-stale-tutorial' }
  /** the record fetch resolved with data. */
  | { kind: 'success'; data: unknown }
  /** the record fetch rejected / 404'd. */
  | { kind: 'error' }

export type DetailFetchResolution = {
  /** whether the detail view should stay open. */
  detailOpen: boolean
  /** the loaded record, or null. */
  detailData: unknown | null
  /** non-null ⇒ navigate the hash back to this list route (e.g. '/projects'). */
  hash: string | null
}

/**
 * Pure model of what a record-detail deep-link effect must do so the
 * `detailOpen && !detailData` spinner can NEVER hang. On success the detail
 * stays open with data; on a skipped stale-tutorial id OR any fetch error the
 * detail is CLOSED and the hash bounces to `listRoute` (e.g. '/projects',
 * '/deals'), guaranteeing the spinner condition resolves to false.
 */
export function resolveDetailFetch(
  outcome: DetailFetchOutcome,
  listRoute: string,
): DetailFetchResolution {
  if (outcome.kind === 'success') {
    return { detailOpen: true, detailData: outcome.data, hash: null }
  }
  // skip-stale-tutorial | error → terminal close so the spinner can't hang.
  return { detailOpen: false, detailData: null, hash: listRoute }
}

/**
 * The spinner-hang condition itself: the detail-record loading state renders
 * exactly while the detail is open but has no data. A resolution that leaves
 * this true forever is the bug.
 */
export function detailSpinnerVisible(state: {
  detailOpen: boolean
  detailData: unknown | null
}): boolean {
  return state.detailOpen && !state.detailData
}
